import React from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { TSubmittalGridRow, TMaterialGridRow } from "../grid-utils";

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  // v is guaranteed truthy here by the guard above, and DateUtils.format only
  // returns "" for a falsy input (dayjs yields "Invalid Date" — truthy —
  // otherwise), so the `|| "—"` fallback branch is unreachable.
  /* v8 ignore next */
  return DateUtils.format(v, DATE_FORMAT_MMDDYYYY) || "—";
}

function fmtFloat(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function deltaUnit(delta: number | null | undefined): string {
  // deltaUnit is only ever called behind a `delta != null` guard (see line 74),
  // so the null-delta early return is unreachable from the sole call site.
  /* v8 ignore next */
  if (delta == null) return "";
  return Math.abs(delta) === 1 ? "day" : "days";
}

type Props = {
  row: TSubmittalGridRow | TMaterialGridRow;
};

// Mirrors ActivityInfo from impact-detail-renderer.tsx so the print
// view's Before / After / Delta block reads identically to the one in
// the grid's expanded row.
function ActivitySummary({ row }: Props) {
  const activity = row._activity;
  const isHighAfter = row.risk_after === "HIGH";
  const delta = row.delta;

  return (
    <div className="space-y-4 print:space-y-4">
      {row.risk_assessment ? (
        <div className="text-[12px] print:text-[8pt] print:leading-tight">
          <span className="text-gray-600 font-medium">
            Risk assessment after Schedule Change:{" "}
          </span>
          <span
            className={
              isHighAfter ? "text-orange-700 font-semibold" : "text-gray-900"
            }
          >
            {row.risk_assessment}
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-6 print:gap-4">
        <div className="flex-1 min-w-[260px] max-w-[360px]">
          <CompareBlock
            title="Before"
            dateLabel="Old Activity Date"
            dateValue={fmtDate(activity?.old_start)}
            floatValue={fmtFloat(row.float_before)}
            variant="before"
          />
        </div>
        <div className="flex-1 min-w-[260px] max-w-[360px]">
          <CompareBlock
            title="After"
            dateLabel="New Activity Date"
            dateValue={fmtDate(activity?.new_start)}
            floatValue={fmtFloat(row.float_after)}
            variant="after"
          />
        </div>
        <div className="flex-col font-medium shrink-0">
          <div className="text-black text-opacity-60 font-medium text-xs">
            Delta
          </div>
          <div className="flex space-x-1 text-sm print:text-[10pt] text-black text-opacity-60 font-normal mt-2 print:mt-1 h-[38px] print:h-[32px] items-center">
            <span>{delta ?? "—"}</span>
            {delta != null ? <span>{deltaUnit(delta)}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

type CompareBlockProps = {
  title: string;
  dateLabel: string;
  dateValue: string;
  floatValue: string;
  variant: "before" | "after";
};

// Single 38-px band (same dimensions as the grid's expanded detail row),
// with a plain sentence-case title floating above it. Before uses gray
// tones, After uses blue — same colour mapping as `activityInfoDiv`.
function CompareBlock({
  title,
  dateLabel,
  dateValue,
  floatValue,
  variant
}: CompareBlockProps) {
  const bgClass = variant === "before" ? "bg-gray-50" : "bg-blue-50";
  const accentClass = variant === "before" ? "bg-gray-200" : "bg-blue-200";
  return (
    <div className="flex flex-col space-y-2">
      <div className="text-black text-opacity-60 font-medium text-xs">
        {title}
      </div>
      <div
        className={`flex justify-between w-full text-sm print:text-[10pt] h-[38px] print:h-[32px] items-center ${bgClass}`}
      >
        <div className="flex items-center gap-2 w-full px-2 min-w-0">
          <div className={`-ml-2 h-full w-1 shrink-0 ${accentClass}`} />
          <div className="flex truncate gap-2 min-w-0">
            <span className="text-ciq-gray-950 font-medium shrink-0">
              {dateLabel}:
            </span>
            <span className="text-black text-opacity-60 font-normal truncate">
              {dateValue}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 mr-2 h-full shrink-0">
          <div className="text-ciq-gray-950 font-medium">Float:</div>
          <div className="text-black text-opacity-60 font-normal">
            {floatValue}
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ActivitySummary);
